import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  MenuItem,
  Box,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  InputAdornment,
  DialogTitle,
  DialogContentText,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import { FiX, FiPlus, FiTrash2, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { format } from 'date-fns';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

// Add custom style for margin-left
const customInputMarginStyle = `
.css-13rb8h2-MuiFormControl-root-MuiTextField-root .MuiOutlinedInput-root {
  margin-left: 10px;
}
.css-modb35-MuiFormControl-root-MuiTextField-root .MuiOutlinedInput-root {
  margin-left: 10px;
}
.css-13cfxxi-MuiFormLabel-root-MuiInputLabel-root {
  position: static;
}
.css-jir51n-MuiInputBase-root-MuiOutlinedInput-root {
  margin-left: 10px;
}
/* Make gender and language select input fully clickable */
.gender-language-select .MuiInputBase-root {
  cursor: pointer;
  width: 100%;
}
`;

const ImageUploadSection = ({ title, images, onImagesChange, maxImages = 5 }) => {
  const [draggedItem, setDraggedItem] = useState(null);

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    if (images.length + newFiles.length > maxImages) {
      alert(`Maximum ${maxImages} images allowed`);
      return;
    }
    onImagesChange([...images, ...newFiles]);
  };

  const handleRemoveImage = (index) => {
    const newImages = [...images];
    newImages.splice(index, 1);
    onImagesChange(newImages);
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = 'move';
    e.target.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    setDraggedItem(null);
    e.target.style.opacity = '1';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index) return;

    const newImages = [...images];
    const draggedImage = newImages[draggedItem];
    newImages.splice(draggedItem, 1);
    newImages.splice(index, 0, draggedImage);
    onImagesChange(newImages);
  };

  return (
    <VuiBox mb={3}>
      <VuiTypography variant="button" color="text" fontWeight="bold" mb={1}>
        {title}
      </VuiTypography>
      <VuiBox
        sx={{
          background: 'rgba(255,255,255,0.02)',
          borderRadius: '12px',
          p: 2,
        }}
      >
        <Grid container spacing={2}>
          {images.map((image, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Box
                draggable
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, index)}
                sx={{
                  position: 'relative',
                  width: '100%',
                  paddingTop: '100%',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  border: '2px solid #353570',
                  cursor: 'move',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  },
                  '&:active': {
                    cursor: 'grabbing',
                  },
                }}
              >
                <img
                  src={typeof image === 'string' ? image : URL.createObjectURL(image)}
                  alt={`${title} ${index + 1}`}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    pointerEvents: 'none',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    display: 'flex',
                    gap: 1,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveImage(index)}
                    sx={{
                      color: 'white',
                      width: 28,
                      height: 28,
                      background: 'rgba(229,57,53,0.9)',
                      '&:hover': {
                        background: 'rgba(229,57,53,1)',
                      },
                    }}
                  >
                    <FiTrash2 size={14} />
                  </IconButton>
                </Box>
              </Box>
            </Grid>
          ))}
          {images.length < maxImages && (
            <Grid item xs={12} sm={6} md={4}>
              <Box
                component="label"
                sx={{
                  width: '100%',
                  paddingTop: '100%',
                  borderRadius: '12px',
                  border: '2px dashed #353570',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  position: 'relative',
                  '&:hover': {
                    borderColor: '#2196f3',
                  },
                }}
              >
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />
                <FiPlus
                  size={32}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: '#353570',
                  }}
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </VuiBox>
    </VuiBox>
  );
};

const ClientEditModal = ({ open, onClose, client, onSave }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    birth_date: null,
    lang: 'ar',
    gender: 'male',
    profile_photos: [],
    // Removed identification_images and driver_license
  });

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    if (client) {
      setFormData({
        first_name: client.first_name || '',
        last_name: client.last_name || '',
        phone: client.phone || '',
        birth_date: client.birth_date ? new Date(client.birth_date) : null,
        lang: client.lang || 'ar',
        gender: client.gender || 'male',
        profile_photos: client.profile_photos || [],
        // Removed identification_images and driver_license
      });
    }
  }, [client]);

  const handleInputChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      birth_date: date,
    }));
  };

  const handleImagesChange = (field) => (newImages) => {
    setFormData((prev) => ({
      ...prev,
      [field]: newImages,
    }));
  };

  const convertImageToFile = async (imageUrl) => {
    try {
      // If it's already a File object, return it
      if (imageUrl instanceof File) {
        return imageUrl;
      }

      // If it's a string URL, fetch it directly
      if (typeof imageUrl === 'string') {
        try {
          // Extract the path after /storage/
          const storagePath = imageUrl.split('/storage/')[1];
          if (!storagePath) {
            throw new Error('Invalid image URL format');
          }

          // Construct the new URL using the /image/ endpoint
          const newImageUrl = `https://api.csc-taxi.tech/image/${storagePath}`;

          const response = await fetch(newImageUrl);
          if (!response.ok) {
            throw new Error(`Failed to fetch image: ${response.statusText}`);
          }

          const blob = await response.blob();
          const originalFilename = imageUrl.split('/').pop().split('?')[0];
          const filename = originalFilename || `image_${Date.now()}.jpg`;

          // Create a new File object from the blob
          return new File([blob], filename, { type: blob.type || 'image/jpeg' });
        } catch (error) {
          console.error('Error fetching image:', {
            url: imageUrl,
            error: error.message
          });
          throw error;
        }
      }

      return null;
    } catch (error) {
      console.error('Error converting image to file:', {
        url: imageUrl,
        error: error.message,
        stack: error.stack
      });
      throw error;
    }
  };

  const handleSubmit = async () => {
    try {
      const formDataToSend = new FormData();

      // Add text fields
      Object.keys(formData).forEach(key => {
        // Only include profile_photos for images
        if (key !== 'profile_photos') {
          if (key === 'birth_date' && formData[key]) {
            formDataToSend.append(key, format(formData[key], 'yyyy-MM-dd'));
          } else {
            formDataToSend.append(key, formData[key]);
          }
        }
      });

      // Process profile photos field
      const field = 'profile_photos';
      try {
        console.log(`Processing ${field} images:`, formData[field]);

        // Convert all images to files, including existing ones
        const imageFiles = await Promise.all(
          formData[field].map(async (image, index) => {
            try {
              const file = await convertImageToFile(image);
              if (!file) {
                throw new Error(`Failed to convert image ${index} for ${field}`);
              }
              return file;
            } catch (error) {
              console.error(`Error converting image ${index} for ${field}:`, {
                image,
                error: error.message
              });
              throw error;
            }
          })
        );

        // Add all files to formData
        imageFiles.forEach((file, index) => {
          if (file instanceof File) {
            console.log(`Adding file ${index} to ${field}:`, {
              name: file.name,
              type: file.type,
              size: file.size
            });
            formDataToSend.append(`${field}[]`, file);
          }
        });
      } catch (error) {
        console.error(`Error processing ${field}:`, {
          error: error.message,
          stack: error.stack
        });
        throw error; // Re-throw to prevent partial updates
      }

      await onSave(formDataToSend);
      onClose();
    } catch (error) {
      console.error('Error saving client:', { // Updated text
        error: error.message,
        stack: error.stack
      });
      throw error; // Re-throw to let the parent component handle the error
    }
  };

  const handleDeleteClick = () => {
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      // Updated API endpoint for deleting client
      await axios.delete(`https://api.csc-taxi.tech/api/admin/client/${client.id}`);
      setDeleteConfirmOpen(false);
      onClose();
      // You might want to add a callback to refresh the client list
    } catch (error) {
      console.error('Error deleting client:', error); // Updated text
      // Handle error appropriately
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
  };

  return (
    <>
      <style>{customInputMarginStyle}</style>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: '#191932',
            color: '#fff',
            borderRadius: '18px',
            boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
          },
        }}
      >
        <VuiBox
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={4}
          pt={4}
          pb={2}
          sx={{
            borderBottom: '1px solid rgba(255,255,255,0.07)',
            background: 'linear-gradient(90deg, #22223a 60%, #1a1a2e 100%)',
            borderTopLeftRadius: '18px',
            borderTopRightRadius: '18px',
          }}
        >
          <VuiTypography variant="h4" color="white" fontWeight="bold">
            Edit Client {/* Updated text */}
          </VuiTypography>
          <Box display="flex" gap={1}>
            <IconButton
              onClick={handleDeleteClick}
              sx={{
                color: '#d32f2f',
                '&:hover': {
                  background: 'rgba(211, 47, 47, 0.1)',
                },
              }}
            >
              <FiTrash2 size={20} />
            </IconButton>
            <IconButton
              onClick={onClose}
              sx={{
                color: '#fff',
                '&:hover': {
                  background: 'rgba(255,255,255,0.1)',
                },
              }}
            >
              <FiX size={24} />
            </IconButton>
          </Box>
        </VuiBox>

        <DialogContent sx={{ px: 4, pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={handleInputChange('first_name')}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': {
                      borderColor: 'rgba(255,255,255,0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255,255,255,0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2196f3',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255,255,255,0.7)',
                    position: 'static',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={handleInputChange('last_name')}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': {
                      borderColor: 'rgba(255,255,255,0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255,255,255,0.3)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#2196f3',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255,255,255,0.7)',
                    position: 'static',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={handleInputChange('phone')}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': {
                      borderColor: 'rgba(255,255,255,0.2)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255,255,255,0.3)',
                    },
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#2196f3',
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255,255,255,0.7)',
                    position: 'static',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Birth Date"
                  value={formData.birth_date}
                  onChange={handleDateChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      variant="outlined"
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          color: '#fff',
                          '& fieldset': {
                            borderColor: 'rgba(255,255,255,0.2)',
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(255,255,255,0.3)',
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#2196f3',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(255,255,255,0.7)',
                          position: 'static',
                        },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <CalendarTodayIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel shrink sx={{ color: 'rgba(255,255,255,0.7)' }}>Language</InputLabel>
                <Select
                  label="Language"
                  value={formData.lang}
                  onChange={handleInputChange('lang')}
                  input={<OutlinedInput notched label="Language" sx={{ color: '#fff' }} />}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#fff',
                      '& fieldset': {
                        borderColor: 'rgba(255,255,255,0.2)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255,255,255,0.3)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2196f3',
                      },
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255,255,255,0.7)',
                    },
                  }}
                >
                  <MenuItem value="ar">Arabic</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
              <FormControl fullWidth variant="outlined">
                <InputLabel shrink sx={{ color: 'rgba(255,255,255,0.7)' }}>Gender</InputLabel>
                <Select
                  label="Gender"
                  value={formData.gender}
                  onChange={handleInputChange('gender')}
                  input={<OutlinedInput notched label="Gender" sx={{ color: '#fff' }} />}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#fff',
                      '& fieldset': {
                        borderColor: 'rgba(255,255,255,0.2)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(255,255,255,0.3)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#2196f3',
                      },
                    },
                    '& .MuiSelect-icon': {
                      color: 'rgba(255,255,255,0.7)',
                    },
                  }}
                >
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Keep only Profile Photos */}
          <ImageUploadSection
            title="Profile Photos"
            images={formData.profile_photos}
            onImagesChange={handleImagesChange('profile_photos')}
            maxImages={5}
          />

          {/* Removed Identification Images */}
          {/* Removed Driver License Images */}

        </DialogContent>

        <DialogActions sx={{ px: 4, pb: 4 }}>
          <Button
            onClick={onClose}
            sx={{
              color: 'rgba(255,255,255,0.7)',
              '&:hover': {
                background: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              background: '#2196f3',
              color: '#fff',
              '&:hover': {
                background: '#1976d2',
              },
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            background: '#191932',
            color: '#fff',
            borderRadius: '18px',
          },
        }}
      >
        <DialogTitle sx={{ color: '#fff' }}>Confirm Delete</DialogTitle> {/* Updated text */}
        <DialogContent>
          <DialogContentText sx={{ color: 'rgba(255,255,255,0.7)' }}>
            Are you sure you want to delete this client? This action cannot be undone. {/* Updated text */}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={handleDeleteCancel}
            sx={{
              color: 'rgba(255,255,255,0.7)',
              '&:hover': {
                background: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{
              background: '#d32f2f',
              '&:hover': {
                background: '#b00036',
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClientEditModal; 